import { useEffect, useState, useCallback, useRef } from 'react';
import { useGHLAuth } from './hooks/useGHLAuth';
import { useGHLIframe } from './hooks/useGHLIframe';
import { UploadForm } from './components/UploadForm';
import { BatchList } from './components/BatchList';
import { BillingButton } from './components/BillingButton';
import { WalletPage } from './components/WalletPage';
import { DashboardPage } from './components/dashboard';
import { ChangePlanModal } from './components/ChangePlanModal';
import { Toast } from './components/Toast';
import { TrustScoreWarningBanner } from './components/TrustScoreWarningBanner';
import NotificationBanners from './components/NotificationBanners';
import NotificationBell from './components/NotificationBell';
import { NotificationsProvider } from './hooks/useNotifications';
import { fetchWithAuthRecovery } from './lib/fetch-with-auth-recovery';
import { ArrowLeft } from 'lucide-react';

type AppPage = 'main' | 'wallet' | 'dashboard';

// Types for billing mode tracking
interface SetupStatus {
  has_stripe_customer: boolean;
  has_payment_method: boolean;
  has_plan: boolean;
}

interface BillingInfo {
  wallet_balance: number;
  billing_mode: 'ghl' | 'wallet';
  has_active_plan: boolean;
  wallet_setup_complete: boolean | null;
  setup_status: SetupStatus | null;
  current_plan: {
    id: string;
    plan_name: string;
    plan_type: string;
    includes_mobile: boolean;
    billing_period: string;
    price: number;
  } | null;
  billing_cycle_end: string | null;
  has_payment_method: boolean;
  scheduled_plan_id: string | null;
  scheduled_plan_name: string | null;
  scheduled_plan_change_date: string | null;
  cancellation_scheduled_date: string | null;
  is_internal?: boolean;
  billing_suspended_at: string | null;
  suspension_cause?: 'plan' | 'balance' | 'both' | null;
  plan_past_due?: boolean;
  plan_expired?: boolean;
  subscription_payment_status?: {
    state: 'current' | 'failing' | 'lapsed';
    failed_amount: number | null;
    invoice_date: string | null;
    next_retry_at: string | null;
    attempt_count: number | null;
    hosted_invoice_url: string | null;
    lapsed_plan_name: string | null;
    suspend_after_attempts: number;
  };
}

export default function App() {
  const { context: ghlContext, isLoading: ghlLoading, error: ghlError } = useGHLIframe();
  const { locationId, isAuthenticated } = useGHLAuth();
  const [batches, setBatches] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [totalCountAllTime, setTotalCountAllTime] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [dbError, setDbError] = useState<string | null>(null);
  const [showUploadForm, setShowUploadForm] = useState(false);

  // Page navigation
  const [currentPage, setCurrentPage] = useState<AppPage>('main');
  const [initialNavDone, setInitialNavDone] = useState(false);
  const [previousPage, setPreviousPage] = useState<AppPage>('dashboard'); // Track where user came from for back navigation

  // Billing info for header button
  const [billingBalance, setBillingBalance] = useState<number | null>(null);
  const [billingLoading, setBillingLoading] = useState(false);

  // Billing mode tracking for wallet vs GHL
  const [billingInfo, setBillingInfo] = useState<BillingInfo | null>(null);
  const [billingMode, setBillingMode] = useState<'ghl' | 'wallet' | null>(null);
  const [hasActivePlan, setHasActivePlan] = useState<boolean>(true);
  const [walletSetupComplete, setWalletSetupComplete] = useState<boolean | null>(null);
  const [setupStatus, setSetupStatus] = useState<SetupStatus | null>(null);

  // Force plan modal when wallet mode but no plan
  const [forcePlanModal, setForcePlanModal] = useState(false);
  const [showChangePlanModal, setShowChangePlanModal] = useState(false);

  // Billing suspension overlay
  const [billingSuspended, setBillingSuspended] = useState(false);

  // Toast notification for auto top-up
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

  // Track previous location ID to detect changes
  const previousLocationIdRef = useRef<string | null>(null);

  // CRITICAL: Always prioritize iframe context location ID over localStorage
  // When user switches locations in GHL, iframe context updates but localStorage is stale
  const effectiveLocationId = ghlContext?.locationId || locationId;
  
  // Detect location changes and force reload to get fresh context
  useEffect(() => {
    if (ghlContext?.locationId) {
      const currentLocationId = ghlContext.locationId;
      const previousLocationId = previousLocationIdRef.current;
      
      // If we've seen a location before and it's different now, location changed
      if (previousLocationId && previousLocationId !== currentLocationId) {
        console.log('[App] Location changed:', previousLocationId, '→', currentLocationId);
        
        // Clear any stale localStorage data
        localStorage.removeItem('ghl_location_id');
        localStorage.removeItem('ghl_access_token');
        localStorage.removeItem('ghl_refresh_token');
        
        // Force reload to get fresh context from GHL
        window.location.reload();
      }
      
      // Update the ref with current location ID
      previousLocationIdRef.current = currentLocationId;
    }
  }, [ghlContext?.locationId]);
  
  // If we have iframe context with location ID, we can proceed without OAuth for now
  const canProceed = (isAuthenticated && effectiveLocationId) || (ghlContext?.locationId && !ghlLoading);

  const fetchBatches = useCallback(async () => {
    if (!effectiveLocationId) {
      return;
    }

    setLoading(true);
    setDbError(null);

    try {
      // Calculate default date filter (last 14 days)
      const startDate = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString();

      // Use API endpoint with pagination params for initial load
      const params = new URLSearchParams({
        locationId: effectiveLocationId,
        limit: '10',
        offset: '0',
        startDate: startDate,
      });

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/batches?${params.toString()}&_t=${Date.now()}`,
        {
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache'
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({})) as { error?: string };
        console.error('[App] API error:', errorData);
        setDbError(`API error: ${errorData.error || response.statusText}. Please try again.`);
      } else {
        const result = await response.json() as { batches: any[]; totalCount?: number; totalCountAllTime?: number | null };
        setBatches(result.batches || []);
        setTotalCount(result.totalCount ?? result.batches?.length ?? 0);
        const allTime = result.totalCountAllTime ?? null;
        setTotalCountAllTime(allTime);
        // Only auto-open the upload form when there are truly no batches at all.
        // If allTime > 0 (older batches exist outside the 14-day window), let
        // BatchList show the "expand date range" empty state instead. Fall back
        // to the legacy behavior when allTime is null (API hasn't been deployed).
        const noBatchesAtAll = allTime === null
          ? (!result.batches || result.batches.length === 0)
          : allTime === 0;
        if (noBatchesAtAll) {
          setShowUploadForm(true);
        }
      }
    } catch (err) {
      console.error('[App] Fetch error:', err);
      setDbError('An unexpected error occurred. Please contact support.');
    }

    setLoading(false);
  }, [effectiveLocationId]);

  // Fetch billing balance and mode info for header button
  // silent=true skips loading state (used for interval refreshes to avoid flashing)
  const fetchBillingBalance = useCallback(async (silent = false) => {
    if (!effectiveLocationId) return;

    if (!silent) {
      setBillingLoading(true);
    }
    try {
      const response = await fetchWithAuthRecovery(
        `${import.meta.env.VITE_API_URL}/billing/${effectiveLocationId}`,
        { credentials: 'include' }
      );

      const data = (await response.json()) as { success: boolean; billing?: BillingInfo };
      if (data.success && data.billing) {
        const newBalance = data.billing.wallet_balance;

        // Only update balance if it changed (prevents unnecessary re-renders)
        setBillingBalance(prev =>
          prev !== newBalance ? newBalance : prev
        );
        setBillingInfo(data.billing);
        setBillingMode(data.billing.billing_mode);
        setHasActivePlan(data.billing.has_active_plan);
        setWalletSetupComplete(data.billing.wallet_setup_complete);
        setSetupStatus(data.billing.setup_status);
      }
    } catch (err) {
      console.error('[App] Billing fetch error:', err);
    } finally {
      if (!silent) {
        setBillingLoading(false);
      }
    }
  }, [effectiveLocationId]);

  useEffect(() => {
    if (canProceed && effectiveLocationId) {
      fetchBatches();
      fetchBillingBalance();
    }
  }, [canProceed, effectiveLocationId, fetchBatches, fetchBillingBalance]);

  // Auto-refresh billing balance every 30 seconds for background changes
  // (communication charges, phone line billing, etc.)
  useEffect(() => {
    if (!canProceed || !effectiveLocationId || billingMode !== 'wallet') {
      return;
    }

    const intervalId = setInterval(() => {
      fetchBillingBalance(true); // silent=true to avoid button flashing
    }, 30000); // 30 seconds

    return () => clearInterval(intervalId);
  }, [canProceed, effectiveLocationId, billingMode, fetchBillingBalance]);

  // Auto-open plan modal when wallet mode + no active plan (skip for internal users)
  useEffect(() => {
    if (billingMode === 'wallet' && !hasActivePlan && !billingInfo?.is_internal) {
      setForcePlanModal(true);
    } else {
      setForcePlanModal(false);
    }
  }, [billingMode, hasActivePlan, billingInfo?.is_internal]);

  // Show suspension overlay when account is suspended (skip for internal users)
  useEffect(() => {
    if (billingInfo?.billing_suspended_at && !billingInfo?.is_internal) {
      setBillingSuspended(true);
    } else {
      setBillingSuspended(false);
    }
  }, [billingInfo?.billing_suspended_at, billingInfo?.is_internal]);

  // Auto-navigate to dashboard for wallet mode users on initial load
  useEffect(() => {
    if (billingMode === 'wallet' && !initialNavDone) {
      setCurrentPage('dashboard');
      setInitialNavDone(true);
    }
  }, [billingMode, initialNavDone]);

  // Handle plan change from modal
  const handlePlanChanged = () => {
    fetchBillingBalance();
    setForcePlanModal(false);
    setShowChangePlanModal(false);
  };

  const handleUploadComplete = async () => {
    setShowUploadForm(false);
    // Small delay to allow backend to finish updating batch counts
    await new Promise(resolve => setTimeout(resolve, 500));
    fetchBatches();
  };

  // Granular batch update without full refresh
  const updateBatch = (batchId: string, updates: Partial<any>) => {
    setBatches(prev => 
      prev.map(batch => 
        batch.id === batchId ? { ...batch, ...updates } : batch
      )
    );
  };

  if (ghlLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">OAK List Scrubber</h1>
          <p className="text-gray-600">Loading application context...</p>
        </div>
      </div>
    );
  }

  if (ghlError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">OAK List Scrubber</h1>
          <p className="text-red-600 mb-4">{ghlError}</p>
          <p className="text-gray-600 text-sm">
            Make sure this page is accessed through Oasis Auto Konnect.
          </p>
        </div>
      </div>
    );
  }

  // Show authentication screen only if we don't have iframe context
  if (!isAuthenticated && !ghlContext?.locationId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">OAK List Scrubber</h1>
          <p className="text-gray-600 mb-4">Authenticating with Oasis Auto Konnect...</p>
          <button
            onClick={() => window.location.href = `https://marketplace.gohighlevel.com/oauth/chooselocation?response_type=code&redirect_uri=${encodeURIComponent(window.location.origin + '/oauth/callback')}&client_id=${import.meta.env.VITE_GHL_CLIENT_ID}&scope=contacts.readonly contacts.write locations.readonly businesses.readonly invoices.write oauth.readonly payments/orders.write payments/orders.readonly payments/orders.collectPayment charges.write marketplace-installer-details.readonly phonenumbers.read`}
            className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Connect to Oasis Auto Konnect
          </button>
          <p className="text-gray-500 text-sm mt-4">
            If you're accessing this through Oasis Auto Konnect, please wait...
          </p>
        </div>
      </div>
    );
  }

  // Wait for billing mode to be determined before showing any page
  // This prevents the "bounce" where wallet users briefly see BatchList before Dashboard
  if (billingMode === null) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">OAK List Scrubber</h1>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Handle navigation back from wallet page
  const handleBackFromWallet = () => {
    // Go back to wherever user came from (dashboard or list scrubber)
    setCurrentPage(previousPage);
    // Refresh billing balance when returning from wallet
    fetchBillingBalance();
  };

  // Navigate to wallet page, tracking where we came from
  const navigateToWallet = (fromPage: AppPage) => {
    setPreviousPage(fromPage);
    setCurrentPage('wallet');
  };

  // Compute effective page - wallet users start on dashboard immediately (no bounce)
  // This avoids the flash where useEffect hasn't run yet but billingMode is known
  const effectivePage = (billingMode === 'wallet' && !initialNavDone) ? 'dashboard' : currentPage;

  // Show wallet page if navigated there
  if (effectivePage === 'wallet' && effectiveLocationId) {
    return (
      <WalletPage
        locationId={effectiveLocationId}
        onBack={handleBackFromWallet}
      />
    );
  }

  // Show dashboard page if navigated there (wallet mode users only)
  if (effectivePage === 'dashboard' && effectiveLocationId) {
    return (
      <NotificationsProvider locationId={effectiveLocationId}>
        <TrustScoreWarningBanner locationId={effectiveLocationId} />
        <NotificationBanners />
        {!billingInfo?.is_internal && billingInfo?.subscription_payment_status &&
          (billingInfo.subscription_payment_status.state === 'failing' ||
            billingInfo.subscription_payment_status.state === 'lapsed') && (
          <div className="bg-red-50 border-b border-red-200 px-4 py-3">
            <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                </svg>
                <p className="text-sm text-red-800">
                  {billingInfo.subscription_payment_status.state === 'lapsed'
                    ? 'Your plan was cancelled because a payment could not be collected. Re-subscribe to restore your service.'
                    : billingInfo.subscription_payment_status.attempt_count != null
                      ? `Your plan payment didn’t go through (attempt ${billingInfo.subscription_payment_status.attempt_count} of ${billingInfo.subscription_payment_status.suspend_after_attempts}). Resolve it now to avoid your account being suspended.`
                      : 'There’s a problem with your plan payment. Please resolve it to avoid your account being suspended.'}
                </p>
              </div>
              <button
                onClick={() => navigateToWallet('dashboard')}
                className="flex-shrink-0 px-4 py-1.5 bg-red-600 text-white text-sm font-semibold rounded hover:bg-red-700 transition-colors"
              >
                {billingInfo.subscription_payment_status.state === 'lapsed' ? 'View Plans' : 'Resolve Payment'}
              </button>
            </div>
          </div>
        )}
        <DashboardPage
          locationId={effectiveLocationId}
          onNavigateToScrubber={() => setCurrentPage('main')}
          onNavigateToWallet={() => navigateToWallet('dashboard')}
          billingMode={billingMode}
          billingBalance={billingBalance}
          billingLoading={billingLoading}
          hasActivePlan={hasActivePlan}
          isInternal={billingInfo?.is_internal}
        />
      </NotificationsProvider>
    );
  }

  return (
    <NotificationsProvider locationId={effectiveLocationId ?? null}>
      {effectiveLocationId && <TrustScoreWarningBanner locationId={effectiveLocationId} />}
      <NotificationBanners />
      <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <header className="mb-8 flex justify-between items-start">
          <div className="flex items-center gap-4">
            {/* Back button only for wallet mode users (they came from Dashboard) */}
            {billingMode === 'wallet' && (
              <button
                onClick={() => setCurrentPage('dashboard')}
                className="p-2 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
            )}
            <div>
              <h1 className="text-3xl font-bold text-gray-900">OAK List Scrubber</h1>
              <p className="text-gray-600 mt-2">
                Validate phone numbers before sending messages to them
              </p>
            </div>
          </div>
          {/* Right-hand controls. The bell sits beside the wallet button and
              shows for every user, including non-wallet mode — the scrubber is
              home for those accounts, so it's their only route to notices. */}
          <div className="flex items-center gap-3">
            <NotificationBell />
            {/* Wallet button only for wallet mode (Dashboard is now home, no need for Dashboard button here) */}
            {billingMode === 'wallet' && (
              <BillingButton
                onClick={() => navigateToWallet('main')}
                balance={billingBalance}
                loading={billingLoading}
              />
            )}
          </div>
        </header>

        {dbError && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 font-medium">Database Error</p>
            <p className="text-red-600 text-sm mt-1">{dbError}</p>
            <p className="text-red-500 text-xs mt-2">
              Please contact support if this issue persists.
            </p>
          </div>
        )}

        {/* Main content - hidden when wallet mode + no plan (modal is forced open), unless internal user */}
        {billingMode === 'wallet' && !hasActivePlan && !billingInfo?.is_internal ? (
          <div className="flex items-center justify-center h-64 text-gray-500">
            {billingInfo?.plan_expired ? (
              <div className="text-center">
                <p className="text-red-600 font-semibold text-lg mb-2">Subscription Payment Failed</p>
                <p className="text-gray-600 mb-4">Your plan payment could not be processed. Please update your payment method to restore access.</p>
                <button
                  onClick={() => setCurrentPage('wallet')}
                  className="px-6 py-2.5 bg-blue-600 text-white font-semibold rounded hover:bg-blue-700 transition-colors"
                >
                  Update Payment Method
                </button>
              </div>
            ) : (
              <p>Please select a plan to continue</p>
            )}
          </div>
        ) : (
          <div className="space-y-8">
            {showUploadForm ? (
              <UploadForm
                locationId={effectiveLocationId!}
                onUploadComplete={handleUploadComplete}
                onCancel={() => setShowUploadForm(false)}
              />
            ) : (
              <div className="flex justify-end mb-4">
                <button
                  onClick={() => setShowUploadForm(true)}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Upload Contact List
                </button>
              </div>
            )}

            {loading ? (
              <div className="text-center py-12">
                <p className="text-gray-600">Loading batches...</p>
              </div>
            ) : (
              <BatchList
                batches={batches}
                onUpdate={fetchBatches}
                onBatchUpdate={updateBatch}
                locationId={effectiveLocationId || undefined}
                initialTotalCount={totalCount}
                initialTotalCountAllTime={totalCountAllTime}
                billingMode={billingMode}
                walletSetupComplete={walletSetupComplete}
                setupStatus={setupStatus}
                currentBalance={billingBalance}
                isInternal={billingInfo?.is_internal}
                onRefreshBillingStatus={fetchBillingBalance}
                onShowAutoRechargeToast={() => {
                  setToast({
                    message: 'Auto top-up initiated. Your wallet balance will update shortly.',
                    type: 'info'
                  });
                }}
              />
            )}
          </div>
        )}
      </div>

      {/* ChangePlanModal - forced open when wallet mode + no plan */}
      {effectiveLocationId && billingInfo && (
        <ChangePlanModal
          isOpen={showChangePlanModal || forcePlanModal}
          onClose={() => {
            // Only allow close if not forced (user has a plan)
            if (!forcePlanModal) {
              setShowChangePlanModal(false);
            }
          }}
          locationId={effectiveLocationId}
          billingInfo={billingInfo}
          onPlanChanged={handlePlanChanged}
          showCloseButton={!forcePlanModal}
        />
      )}

      {/* Billing suspension overlay - un-dismissable, directs to wallet page */}
      {billingSuspended && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6 text-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Account Suspended</h2>
            <p className="text-gray-600 mb-6">
              {billingInfo?.suspension_cause === 'plan'
                ? 'Your account has been suspended because your plan payment could not be processed. Please resolve your plan payment to reactivate your account and resume processing.'
                : billingInfo?.suspension_cause === 'both'
                ? 'Your account has been suspended due to an unpaid plan payment and a negative wallet balance. Please resolve your plan payment and add funds to your wallet to reactivate your account.'
                : 'Your account has been suspended due to a negative balance. Please add funds to your wallet to reactivate your account and resume processing.'}
            </p>
            <button
              onClick={() => {
                setCurrentPage('wallet');
                setBillingSuspended(false);
              }}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Go to Wallet
            </button>
          </div>
        </div>
      )}

      {/* Toast notification for auto top-up */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          duration={8000}
          onClose={() => setToast(null)}
        />
      )}
    </div>
    </NotificationsProvider>
  );
}
